import React, { Dispatch, useEffect, useReducer, useRef, useState } from "react";

import { ComboBox, ComboBoxCloseEvent, ComboBoxFilterChangeEvent, DropDownListChangeEvent, MultiSelectChangeEvent, MultiSelectFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { Input, InputChangeEvent, NumericTextBox, NumericTextBoxChangeEvent } from "@progress/kendo-react-inputs";
import ApiCommunicator, { useApiService } from "@selas/api-communication";
import { IAction, IEntity } from "@selas/models";
import { getInitialState } from "@selas/state-management";
import {
	EntityEditor,
	EnumDropDownList,
	handleChange,
	IAddEntityScreenProps,
	IEditEntityScreenProps,
	ManageableField,
	ManageableMultiSelect,
	SearchBox,
	setEntity,
	TextArea
} from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import map from "lodash/map";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { hasPermission } from "../../../services/authentication";
import { catalogueServiceReducer, invoicingMomentReducer, languageReducer, projectParticipantServiceReducer, serviceTypeReducer } from "../../../state/reducers";
import { Permission, ProjectServiceStatus } from "../../../utils/enums";
import { ICatalogueService, IInvoicingMoment, ILanguage, IProjectParticipantService, IProjectServiceBase, IServiceLanguage, IServiceType } from "../../../utils/types/models";
import CatalogueServiceEditor from "../catalogue/catalogueService";
import ServiceTypeEditor from "../catalogue/serviceType";
import InvoicingMomentEditor from "../masterdata/invoicingMoment";
import LanguageEditor from "../personInfo/language";
import { renderProjectServiceStatus } from "./grids/projectServices";
import filter from "lodash/filter";

interface IProps {
	projectId: number;
	participantId: number;
}

const ProjectParticipantServiceEditor: React.FC<(IAddEntityScreenProps<IProjectParticipantService> | IEditEntityScreenProps<IProjectParticipantService>) & IProps> = (
	props: (IAddEntityScreenProps<IProjectParticipantService> | IEditEntityScreenProps<IProjectParticipantService>) & IProps
) => {
	const { t } = useTranslation();

	const initialProjectParticipantService: IProjectParticipantService = {
		id: (props as IEditEntityScreenProps<IProjectParticipantService>).recordId || 0,
		amount: 1,
		code: "",
		invoicingMomentId: null,
		languages: [],
		collectiveHours: 0,
		individualHours: 0,
		numberOfMonths: 0,
		salesPrice: 0,
		projectParticipantId: props.participantId,
		status: ProjectServiceStatus.Forecast,
		typeId: null,
		priority: 1
	};
	const [projectParticipantService, setProjectParticipantService] = useState<IProjectParticipantService>(initialProjectParticipantService);
	const [catalogueService, setCatalogueService] = useState<ICatalogueService>();
	const [dataChanged, setDataChanged] = useState<boolean>(false);

	const [projectParticipantServiceState, projectParticipantServiceDispatch] = useReducer(projectParticipantServiceReducer, getInitialState<IProjectParticipantService>());
	const [serviceTypeState, serviceTypeDispatch] = useReducer(serviceTypeReducer, getInitialState<IServiceType>());
	const [languageState, languageDispatch] = useReducer(languageReducer, getInitialState<ILanguage>());
	const [invoicingMomentState, invoicingMomentDispatch] = useReducer(invoicingMomentReducer, getInitialState<IInvoicingMoment>());
	const [catalogueServiceState, catalogueServiceDispatch] = useReducer(catalogueServiceReducer, getInitialState<ICatalogueService>());

	const firstField: React.MutableRefObject<Input | ComboBox> = useRef();
	const apiService: ApiCommunicator = useApiService();

	useEffect(() => {
		if (projectParticipantServiceState.entity) {
			setProjectParticipantService(projectParticipantServiceState.entity);
		}
	}, [projectParticipantServiceState.entity]);

	useEffect(() => {
		if (hasPermission(Permission.ProjectsAdd, Permission.ProjectsUpdate)) {
			apiService.callApi(languageDispatch, Endpoint.Languages, "GET");
		}
	}, [apiService]);

	function onChange(event: InputChangeEvent | ComboBoxCloseEvent | DropDownListChangeEvent | React.ChangeEvent<HTMLTextAreaElement> | NumericTextBoxChangeEvent): void {
		setProjectParticipantService(handleChange(projectParticipantService, event));
		setDataChanged(true);
	}

	function onCatalogueServiceChange(newCatalogueService: ICatalogueService): void {
		setCatalogueService(newCatalogueService);
		if (newCatalogueService) {
			const newProjectParticipantService: IProjectParticipantService = {
				...newCatalogueService,
				id: projectParticipantService.id,
				projectParticipantId: projectParticipantService.projectParticipantId,
				status: projectParticipantService.status,
				amount: projectParticipantService.amount,
				languages: map(newCatalogueService.languages, (language: IServiceLanguage<ICatalogueService>) => ({
					serviceId: projectParticipantService.id,
					languageId: language.languageId,
					language: language.language
				})),
				priority: projectParticipantService.priority
			};
			updateProjectParticipantService(newProjectParticipantService);
		}
	}

	function setChild<T extends IEntity>(entity: T, field: keyof IProjectParticipantService, idField: keyof IProjectParticipantService): void {
		const newProjectParticipantService: IProjectParticipantService = setEntity(projectParticipantService, entity, field, idField);
		updateProjectParticipantService(newProjectParticipantService);
	}

	function onMultiSelectChange(event: MultiSelectChangeEvent): void {
		const newProjectParticipantService: IProjectParticipantService = cloneDeep(projectParticipantService);

		if (event.value) {
			if (event.target.name === "languages") {
				newProjectParticipantService.languages = [];
				for (const lang of event.value) {
					newProjectParticipantService.languages.push({ serviceId: newProjectParticipantService.id, languageId: lang.id, language: lang });
				}
			}
			updateProjectParticipantService(newProjectParticipantService);
		}
	}

	function addOrUpdateLanguage(language: ILanguage): void {
		const newOpportunityService: IProjectParticipantService = cloneDeep(projectParticipantService);
		const existingServiceLanguage: IServiceLanguage<IProjectServiceBase> = find(newOpportunityService.languages, { languageId: language.id });
		if (existingServiceLanguage) {
			existingServiceLanguage.language = language;
		} else {
			newOpportunityService.languages.push({
				serviceId: newOpportunityService.id,
				languageId: language.id,
				language
			});
		}
		apiService.callApi(languageDispatch, Endpoint.Languages, "GET");
		updateProjectParticipantService(newOpportunityService);
	}

	function updateProjectParticipantService(newProjectParticipantService: IProjectParticipantService): void {
		setProjectParticipantService(newProjectParticipantService);
		setDataChanged(true);
	}

	function onFilterChange(event: ComboBoxFilterChangeEvent | MultiSelectFilterChangeEvent): void {
		let dispatch: Dispatch<IAction>;
		let filterEndpoint: Endpoint;
		switch (event.target.name) {
			case "typeId":
				dispatch = serviceTypeDispatch;
				filterEndpoint = Endpoint.ServiceTypes;
				break;
			case "invoicingMomentId":
				dispatch = invoicingMomentDispatch;
				filterEndpoint = Endpoint.InvoicingMoments;
				break;
			case "catalogueService":
				dispatch = catalogueServiceDispatch;
				filterEndpoint = Endpoint.CatalogueServices;
				break;
			case "languages":
				dispatch = languageDispatch;
				filterEndpoint = Endpoint.Languages;
				break;
		}
		apiService.callApi(dispatch, filterEndpoint, "GET", { search: event.filter.value });
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(projectParticipantService.code)) {
			messages.push(t("fill_in_required_field", { field: t("serviceCode").toLowerCase() }));
		}
		if (!projectParticipantService.typeId) {
			messages.push(t("fill_in_required_field", { field: t("serviceType").toLowerCase() }));
		}
		if (!projectParticipantService.invoicingMomentId) {
			messages.push(t("fill_in_required_field", { field: t("invoicingMoment").toLowerCase() }));
		}
		if (!projectParticipantService.languages || projectParticipantService.languages.length <= 0) {
			messages.push(t("fill_in_required_field", { field: t("languages").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IProjectParticipantService): void {
		if (record && !close) {
			setProjectParticipantService(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IProjectParticipantService>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={projectParticipantService}
			endpoint={Endpoint.ProjectParticipantServices}
			extraUrlParams={{ projectId: props.projectId, participantId: props.participantId }}
			entityState={projectParticipantServiceState}
			entityType={t("projectParticipantService")}
			dispatch={projectParticipantServiceDispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={projectParticipantService.code}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				{!projectParticipantService.id && (
					<ManageableField
						fieldLabel={t("service")}
						recordId={catalogueService?.id}
						addScreen={{ screen: CatalogueServiceEditor, isAllowed: hasPermission(Permission.CatalogueServicesAdd) }}
						editScreen={{ screen: CatalogueServiceEditor, isAllowed: hasPermission(Permission.CatalogueServicesUpdate) }}
						setEntity={(service: ICatalogueService) => onCatalogueServiceChange(service)}
						readOnly={readonly}
					>
						<SearchBox
							name="catalogueService"
							entities={catalogueServiceState.entities}
							isLoading={catalogueServiceState.areEntitiesLoading}
							entityId={catalogueService?.id}
							entity={catalogueService}
							textField="code"
							onFilterChange={onFilterChange}
							onClose={(event: ComboBoxCloseEvent) => onCatalogueServiceChange(event.target.value)}
							onClear={() => setCatalogueService(undefined)}
							disabled={readonly}
							myRef={firstField as React.RefObject<ComboBox>}
						/>
					</ManageableField>
				)}
				<div className="row">
					<div className="col">
						<label className="k-form-field">
							<span>{t("serviceCode")} *</span>
							<Input
								name="code"
								ref={projectParticipantService.id ? (firstField as React.RefObject<Input>) : undefined}
								value={projectParticipantService.code}
								onChange={onChange}
								required
								disabled={readonly}
							/>
						</label>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<ManageableField
							fieldLabel={`${t("serviceType")} *`}
							recordId={projectParticipantService.typeId}
							addScreen={{ screen: ServiceTypeEditor, isAllowed: hasPermission(Permission.ServiceTypesAdd) }}
							editScreen={{ screen: ServiceTypeEditor, isAllowed: hasPermission(Permission.ServiceTypesUpdate) }}
							setEntity={(type: IServiceType) => setChild(type, "type", "typeId")}
							readOnly={readonly}
						>
							<SearchBox
								name="typeId"
								entities={serviceTypeState.entities}
								isLoading={serviceTypeState.areEntitiesLoading}
								entityId={projectParticipantService.typeId}
								entity={projectParticipantService.type}
								required
								textField="name"
								onFilterChange={onFilterChange}
								onClose={onChange}
								onClear={() => setChild(null, "type", "typeId")}
								disabled={readonly}
							/>
						</ManageableField>
					</div>
					<div className="col">
						<ManageableMultiSelect
							fieldLabel={`${t("languages")} *`}
							addScreen={{ screen: LanguageEditor, isAllowed: hasPermission(Permission.LanguagesAdd) }}
							editScreen={{ screen: LanguageEditor, isAllowed: hasPermission(Permission.LanguagesUpdate) }}
							setEntity={addOrUpdateLanguage}
							name="languages"
							data={languageState.entities}
							textField="name"
							dataItemKey="id"
							value={map(projectParticipantService.languages, "language")}
							loading={languageState.areEntitiesLoading}
							onChange={onMultiSelectChange}
							filterable
							onFilterChange={onFilterChange}
							autoClose={false}
							disabled={readonly}
							readOnly={readonly}
							required
						/>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<label className="k-form-field">
							<span>{t("amount")}</span>
							<NumericTextBox name="amount" value={projectParticipantService.amount} onChange={onChange} min={0} disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("priority")}</span>
							<NumericTextBox name="priority" value={projectParticipantService.priority} onChange={onChange} min={1} disabled={true} />
						</label>
					</div>
					<div className="col">
						<div className="k-form-field">
							<span>{t("projectServiceStatus")} *</span>
							<EnumDropDownList
								name="status"
								value={projectParticipantService.status}
								onChange={onChange}
								enum={filter(ProjectServiceStatus, (status) => status !== ProjectServiceStatus.Invoiced || hasPermission(Permission.AllowSettingInvoicedStatus))}
								itemRender={renderProjectServiceStatus}
								required
								disabled={readonly}
							/>
						</div>
					</div>
				</div>
				<label className="k-form-field">
					<span>{t("shortDescription")}</span>
					<TextArea name="shortDescription" value={projectParticipantService.shortDescription} onChange={onChange} disabled={readonly} />
				</label>
				<label className="k-form-field">
					<span>{t("longDescription")}</span>
					<TextArea name="longDescription" value={projectParticipantService.longDescription} onChange={onChange} disabled={readonly} />
				</label>
				<div className="row">
					<div className="col">
						<label className="k-form-field">
							<span>{t("collectiveHours")}</span>
							<NumericTextBox name="collectiveHours" value={projectParticipantService.collectiveHours} onChange={onChange} format="n2" min={0} disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("individualHours")}</span>
							<NumericTextBox name="individualHours" value={projectParticipantService.individualHours} onChange={onChange} format="n2" min={0} disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("numberOfMonths")}</span>
							<NumericTextBox name="numberOfMonths" value={projectParticipantService.numberOfMonths} onChange={onChange} format="n0" min={0} disabled={readonly} />
						</label>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<ManageableField
							fieldLabel={`${t("invoicingMoment")} *`}
							recordId={projectParticipantService.invoicingMomentId}
							addScreen={{ screen: InvoicingMomentEditor, isAllowed: hasPermission(Permission.InvoicingMomentsAdd) }}
							editScreen={{ screen: InvoicingMomentEditor, isAllowed: hasPermission(Permission.InvoicingMomentsUpdate) }}
							setEntity={(invoicingMoment: IInvoicingMoment) => setChild(invoicingMoment, "invoicingMoment", "invoicingMomentId")}
							readOnly={readonly}
						>
							<SearchBox
								name="invoicingMomentId"
								entities={invoicingMomentState.entities}
								isLoading={invoicingMomentState.areEntitiesLoading}
								entityId={projectParticipantService.invoicingMomentId}
								entity={projectParticipantService.invoicingMoment}
								required
								textField="name"
								onFilterChange={onFilterChange}
								onClose={onChange}
								onClear={() => setChild(null, "invoicingMoment", "invoicingMomentId")}
								disabled={readonly}
							/>
						</ManageableField>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("salesPrice")}</span>
							<NumericTextBox name="salesPrice" value={projectParticipantService.salesPrice} onChange={onChange} format="n2" disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("account")}</span>
							<Input name="account" value={projectParticipantService.account} onChange={onChange} disabled={readonly} />
						</label>
					</div>
				</div>
			</div>
		</EntityEditor>
	);
};

export default ProjectParticipantServiceEditor;
